// src/context/walletConnectionHelpers.jsx

/**
 * Helper functions for wallet connection
 * Extracted to improve code organization and maintainability
 */
import { getWalletProvider, getChainInfo, getOkxSolanaProvider } from '../utils/walletUtils';

/**
 * Try connection fallback approaches for OKX Solana
 * @param {Object} provider - The wallet provider
 * @returns {Array|null} Array of accounts or null if not found
 */
export async function tryConnectionFallbacks(provider) {
  console.log("Attempting connection fallbacks for OKX Solana...");
  
  // Fallback 1: Try connect with options (some OKX versions support this)
  try {
    console.log('Trying connect with onlyIfTrusted parameter');
    const altResp = await provider.connect({ onlyIfTrusted: true });
    
    if (altResp && altResp.publicKey) {
      const publicKey = altResp.publicKey.toString ? 
        altResp.publicKey.toString() : 
        altResp.publicKey;
      console.log('Connected with onlyIfTrusted:', [publicKey]);
      return [publicKey];
    } else if (provider.publicKey) {
      const publicKey = provider.publicKey.toString ? 
        provider.publicKey.toString() : 
        provider.publicKey;
      console.log('Found publicKey after onlyIfTrusted connect:', [publicKey]);
      return [publicKey];
    }
  } catch (altError) {
    console.warn('Connect with onlyIfTrusted failed:', altError);
  }
  
  // Fallback 2: Try getAccount method
  if (typeof provider.getAccount === 'function') {
    try {
      console.log('Trying getAccount method');
      const account = await provider.getAccount();
      if (account) {
        const address = account.toString ? account.toString() : account;
        console.log('Got account through getAccount:', [address]);
        return [address];
      }
    } catch (getAccError) {
      console.warn('getAccount method failed:', getAccError);
    }
  }
  
  // Fallback 3: Try getPublicKey method
  if (typeof provider.getPublicKey === 'function') {
    try {
      console.log('Trying getPublicKey method');
      const publicKey = await provider.getPublicKey();
      if (publicKey) {
        const address = publicKey.toString ? publicKey.toString() : publicKey;
        console.log('Got publicKey through getPublicKey:', [address]);
        return [address];
      }
    } catch (getKeyError) {
      console.warn('getPublicKey method failed:', getKeyError);
    }
  }
  
  // Fallback 4: Try signMessage method to trigger account exposure
  if (typeof provider.signMessage === 'function') {
    try {
      console.log('Trying to access publicKey via signMessage preparation');
      
      // Create dummy message object but DON'T actually sign (we'll cancel quickly)
      const dummyMsg = new TextEncoder().encode("Connection Check");
      
      try {
        // This should fail but might populate publicKey
        await Promise.race([
          provider.signMessage(dummyMsg, { skip: true }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 100))
        ]);
      } catch (e) { 
        // Expected error - ignore
      }
      
      // Check if publicKey is now available
      if (provider.publicKey) {
        const publicKey = provider.publicKey.toString ? 
          provider.publicKey.toString() : 
          provider.publicKey;
        console.log('Found publicKey after signMessage preparation:', [publicKey]);
        return [publicKey];
      }
    } catch (signError) {
      console.warn('signMessage preparation failed:', signError);
    }
  }
  
  return null;
}

/**
 * Try alternative connection approaches when standard methods fail
 * @param {Object} provider - The wallet provider
 * @returns {Array|null} Array of accounts or null if not found
 */
export async function tryAlternateApproaches(provider) {
  console.log("Attempting alternate approaches for OKX Solana...");
  
  // Check for direct publicKey property
  if (provider.publicKey) {
    try {
      const publicKey = provider.publicKey.toString ? 
        provider.publicKey.toString() : 
        provider.publicKey;
      console.log('Found public key directly on provider:', [publicKey]);
      return [publicKey];
    } catch (error) {
      console.warn('Direct publicKey access failed:', error);
    }
  }
  
  // Check custom OKX methods that might exist
  const possibleMethods = [
    'requestAccounts',
    'getAccounts',
    'requestAccount',
    'getWalletAccounts',
    'requestSolanaAccounts',
    'getSolanaAccounts',
    'listAccounts'
  ];
  
  for (const method of possibleMethods) {
    if (typeof provider[method] === 'function') {
      try {
        console.log(`Trying ${method} method`);
        const result = await provider[method]();
        
        if (result) {
          if (result.publicKey) {
            const publicKey = result.publicKey.toString ? 
              result.publicKey.toString() : 
              result.publicKey;
            console.log(`Got account through ${method}:`, [publicKey]);
            return [publicKey];
          } else if (Array.isArray(result) && result.length > 0) {
            const firstAccount = result[0];
            if (typeof firstAccount === 'string') {
              console.log(`Got account through ${method}:`, [firstAccount]);
              return [firstAccount];
            } else if (firstAccount && firstAccount.publicKey) {
              const publicKey = firstAccount.publicKey.toString ? 
                firstAccount.publicKey.toString() : 
                firstAccount.publicKey;
              console.log(`Got account through ${method}:`, [publicKey]);
              return [publicKey];
            } else if (firstAccount && typeof firstAccount.toString === 'function') {
              console.log(`Got account through ${method}:`, [firstAccount.toString()]);
              return [firstAccount.toString()];
            }
          } else if (typeof result === 'string') {
            console.log(`Got account through ${method}:`, [result]);
            return [result];
          } else if (typeof result.toString === 'function') {
            console.log(`Got account through ${method}:`, [result.toString()]);
            return [result.toString()];
          }
        }
      } catch (error) {
        console.warn(`${method} method failed:`, error);
      }
    }
  }
  
  // Additional helper functions are in the walletConnectionMoreHelpers.jsx file
  // to avoid making this file too large
  
  return null;
}

/**
 * Extract common wallet connection error patterns
 * @param {Error} error - The error object
 * @returns {string} A categorized error code
 */
export function categorizeConnectionError(error) {
  const message = error.message.toLowerCase();
  
  if (message.includes('user rejected') || 
      message.includes('user canceled') || 
      message.includes('not authorized') || 
      message.includes('user denied') ||
      message.includes('not approved')) {
    return 'user_rejected';
  }
  
  if (message.includes('timeout') ||
      message.includes('timed out')) {
    return 'timeout';
  }
  
  if (message.includes('disconnected') ||
      message.includes('connection closed')) {
    return 'disconnected';
  }
  
  if (message.includes('not found') ||
      message.includes('no provider') ||
      message.includes('not available')) {
    return 'provider_missing';
  }
  
  if (message.includes('not supported') ||
      message.includes('not implemented')) {
    return 'not_supported';
  }
  
  if (message.includes('public key') ||
      message.includes('publickey') ||
      message.includes('account')) {
    return 'key_missing';
  }
  
  return 'unknown';
}

// Import network switching functions from the dedicated file
export { handleOkxNetworkSwitch } from './walletNetworkHelpers';
